import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';

import { Button, Dialog, DialogContent, DialogTitle, IconButton, TextField, Checkbox, ListItemText, TableContainer, Table, TableHead, TableBody, TableCell, TableRow, Paper, Tooltip as MuiTooltip } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { DatePicker } from '@material-ui/pickers';

import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip } from 'recharts';
import { MdOutlineClose, MdShowChart, MdOutlineTableChart, MdBarChart } from 'react-icons/md';
import format from 'date-fns/format';
import { sub } from 'date-fns';

import { formatLabelValue } from 'app/admin/common/util/CommonUtil';
import defaultLabelStyle from 'app/admin/common/component/DefaultLabelStyle';

import { useOptimizationPipeline } from '../OptimizationPipelineProvider';

const intervalButtons = [{ label: 'All', unit: null }, { label: '1y', unit: { years: 1 } }, { label: '1m', unit: { months: 1 } }, { label: '1w', unit: { weeks: 1 } }, { label: '1d', unit: { days: 1 } }];
const MAX_DEFAULT = 25;

function isNumber(x) {
    return typeof x === 'number' && Number.isFinite(x);
}

function toMs(v) {
    if (!v) return NaN;
    if (v instanceof Date) return v.getTime();
    const t = new Date(v).getTime();
    return Number.isFinite(t) ? t : NaN;
}

function normalizeNumber(v) {
    if (isNumber(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
    if (typeof v === 'boolean') return v ? 1 : 0;
    return null;
}

function colorForIndex(idx, count) {
    if (count <= 1) return '#252f3e';
    return 'hsl(' + ((idx * 60) % 360) + ', 70%, 40%)';
}

function buildMinMaxDates(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return { minDate: null, maxDate: null };

    const sorted = [...rows]
        .filter(Boolean)
        .filter(r => r && r.ts)
        .sort((a, b) => toMs(a.ts) - toMs(b.ts));

    if (!sorted.length) return { minDate: null, maxDate: null };

    const minDate = new Date(sorted[0].ts);
    const maxDate = new Date(sorted[sorted.length - 1].ts);

    if (Number.isNaN(minDate.getTime()) || Number.isNaN(maxDate.getTime())) return { minDate: null, maxDate: null };

    return { minDate, maxDate };
}

function isDynamicCheckOk(row) {
    return row?.finished === true && row?.converged === true && row?.alarm === false;
}

function alarmVariablesCount(row) {
    if (!row?.alarm_variables || typeof row.alarm_variables !== 'object' || Array.isArray(row.alarm_variables)) return 0;
    return Object.keys(row.alarm_variables).length;
}

function formatYesNo(v) {
    return v === true ? 'Yes' : v === false ? 'No' : '-';
}

export default function DynamicValidationHistoryPopup(props) {
    const mainTheme = useSelector(selectMainTheme);
    const { popup, setPopup } = useOptimizationPipeline();

    const { data } = props;

    const [isChartView, setIsChartView] = useState(true);
    const [chartType, setChartType] = useState('line');

    const sortedRows = useMemo(() => {
        const list = Array.isArray(data) ? data : [];

        return [...list]
            .filter(r => r && r.ts)
            .sort((a, b) => toMs(a.ts) - toMs(b.ts));
    }, [data]);

    const allKeys = useMemo(() => {
        const set = new Set();

        sortedRows.forEach((row) => {
            if (row?.variables_to_monitor && typeof row.variables_to_monitor === 'object' && !Array.isArray(row.variables_to_monitor)) {
                Object.entries(row.variables_to_monitor).forEach(([key, value]) => {
                    if (normalizeNumber(value) !== null) set.add(key);
                });
            }
        });

        return Array.from(set).sort((a, b) => a.localeCompare(b, 'hu'));
    }, [sortedRows]);

    const { minDate, maxDate } = useMemo(() => buildMinMaxDates(sortedRows), [sortedRows]);

    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedKeys, setSelectedKeys] = useState([]);

    useEffect(() => {
        if (popup !== 'DYNAMIC_VALIDATION_HISTORY') return;

        setIsChartView(true);
        setChartType('line');

        if (minDate && maxDate) {
            setDateFrom(minDate);
            setDateTo(maxDate);
        } else {
            setDateFrom(null);
            setDateTo(null);
        }

        setSelectedKeys(allKeys.length ? [allKeys[0]] : []);
    }, [popup, minDate, maxDate, allKeys]);

    const handleIntervalSelect = useCallback((unit) => {
        if (!minDate || !maxDate) return;

        if (!unit) {
            setDateFrom(minDate);
            setDateTo(maxDate);
            return;
        }

        const newFrom = sub(maxDate, unit);

        if (newFrom < minDate) {
            setDateFrom(minDate);
            setDateTo(maxDate);
            return;
        }

        setDateFrom(newFrom);
        setDateTo(maxDate);
    }, [minDate, maxDate]);

    const isIntervalAvailable = useCallback((unit) => {
        if (!minDate || !maxDate) return false;
        if (!unit) return true;
        return sub(maxDate, unit) >= minDate;
    }, [minDate, maxDate]);

    const filteredRows = useMemo(() => {
        if (!sortedRows.length || !dateFrom || !dateTo) return [];

        const fromMs = toMs(dateFrom);
        const toMsValue = toMs(dateTo);

        return sortedRows.filter((row) => {
            const t = toMs(row.ts);
            return Number.isFinite(t) && t >= fromMs && t <= toMsValue;
        });
    }, [sortedRows, dateFrom, dateTo]);

    const chartData = useMemo(() => {
        return filteredRows.map((row) => {
            const item = { ts: row.ts };

            selectedKeys.forEach((key) => {
                item[key] = normalizeNumber(row?.variables_to_monitor?.[key]);
            });

            return item;
        });
    }, [filteredRows, selectedKeys]);

    const getTooltip = useCallback(({ active, payload }) => {
        if (!(active && payload && payload.length)) return null;

        const p = payload[0] && payload[0].payload ? payload[0].payload : null;
        if (!p) return null;

        const d = new Date(p.ts);
        const tsLabel = Number.isNaN(d.getTime()) ? String(p.ts) : format(d, 'yyyy.MM.dd. HH:mm:ss');

        return (
            <div className='flex flex-col gap-1 bg-black opacity-80 p-8 text-white text-xs shadow-lg min-w-[240px] max-w-[420px]'>
                <div className='mb-2 font-semibold text-[11px] opacity-80'>{tsLabel}</div>

                {selectedKeys.map((key) => (
                    <div key={key} className='font-medium'>
                        {key}: {p[key] == null || Number.isNaN(p[key]) ? '-' : formatLabelValue(p[key], defaultLabelStyle())}
                    </div>
                ))}
            </div>
        );
    }, [selectedKeys]);

    const IntervalButtons = () => (
        <div className='flex gap-4 items-center'>
            {intervalButtons.map(({ label, unit }) => (
                <MuiTooltip key={label} title={(unit ? 'Last ' : '') + label} arrow>
                    <span>
                        <IconButton size='small' onClick={() => handleIntervalSelect(unit)} disabled={!isIntervalAvailable(unit)}>
                            <span className='text-xs font-semibold p-4'>{label}</span>
                        </IconButton>
                    </span>
                </MuiTooltip>
            ))}

            <MuiTooltip title={chartType === 'line' ? 'Bar chart' : 'Line chart'} arrow>
                <span>
                    <IconButton size='small' onClick={() => setChartType(t => (t === 'line' ? 'bar' : 'line'))} disabled={!isChartView}>
                        {chartType === 'line' ? <MdBarChart size={20} /> : <MdShowChart size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>

            <MuiTooltip title={isChartView ? 'Table view' : 'Chart view'} arrow>
                <span>
                    <IconButton size='small' onClick={() => setIsChartView(v => !v)}>
                        {isChartView ? <MdOutlineTableChart size={20} /> : <MdShowChart size={20} />}
                    </IconButton>
                </span>
            </MuiTooltip>
        </div>
    );

    const hasStableLayout = allKeys.length > 0;

    return (
        <ThemeProvider theme={mainTheme}>
            <Dialog open={popup === 'DYNAMIC_VALIDATION_HISTORY'} onClose={() => setPopup(null)} fullWidth maxWidth={hasStableLayout ? 'lg' : 'sm'}>
                <DialogTitle disableTypography className='border-b flex flex-col px-0'>

                    <div className={'flex items-center justify-between gap-32 px-32 ' + (hasStableLayout ? 'border-b pb-14' : '')}>
                        <div className='whitespace-nowrap'>EDT Dynamic Check - History</div>
                        <div className='flex items-center gap-8'>
                            <Button onClick={() => setPopup(null)}>Close</Button>
                            <IconButton size='small' onClick={() => setPopup(null)}><MdOutlineClose size={20} /></IconButton>
                        </div>
                    </div>

                    {hasStableLayout && (
                        <div className='w-full flex flex-col gap-8 shadow-inner px-20 pt-14'>
                            <div className='w-full flex flex-col gap-8 rounded-xl bg-grey-100 py-10 shadow px-16'>
                                <Autocomplete
                                    className='flex-1'
                                    multiple
                                    options={allKeys}
                                    value={selectedKeys}
                                    onChange={(e, newValue) => setSelectedKeys(Array.isArray(newValue) ? newValue.slice(0, MAX_DEFAULT) : [])}
                                    disableCloseOnSelect
                                    getOptionLabel={(option) => option}
                                    renderOption={(option, { selected }) => {
                                        const parts = String(option).split('.');
                                        const parent = parts.length > 1 ? parts[0] : null;
                                        const child = parts.length > 1 ? parts.slice(1).join('.') : option;

                                        return (
                                            <>
                                                <Checkbox style={{ marginRight: 8 }} checked={selected} />
                                                <ListItemText primary={parent ? (<span><span className='font-semibold opacity-30'>{parent + '.'}</span>{child}</span>) : option} />
                                            </>
                                        );
                                    }}
                                    renderInput={(params) => {
                                        const { InputProps: extractedInputProps, inputProps: extractedInnerInputProps, ...filteredParams } = params;

                                        return (
                                            <TextField
                                                {...filteredParams}
                                                variant='outlined'
                                                label='Search and select monitor keys'
                                                placeholder='Select keys'
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{ ...extractedInputProps, style: { minHeight: 40 }, inputProps: { ...extractedInnerInputProps, style: { padding: '10px 8px', fontSize: '13px', lineHeight: 1.5 } } }}
                                            />
                                        );
                                    }}
                                />

                                <div className='flex-1 flex items-center gap-16'>
                                    <DatePick className='flex-1' label='From' value={dateFrom} minDate={minDate} maxDate={maxDate} onChange={(v) => setDateFrom(v)} />
                                    <DatePick className='flex-1' label='To' value={dateTo} minDate={dateFrom || minDate} maxDate={maxDate} onChange={(v) => setDateTo(v)} />
                                    <IntervalButtons />
                                </div>
                            </div>
                        </div>
                    )}
                </DialogTitle>

                <DialogContent className='w-full flex flex-col p-20 overflow-hidden' style={{ height: hasStableLayout ? '520px' : 'auto' }}>
                    {allKeys.length === 0 && <div className='text-center'>No monitor variables available.</div>}
                    {allKeys.length > 0 && selectedKeys.length === 0 && <div className='text-center'>Select at least one monitor variable.</div>}
                    {allKeys.length > 0 && selectedKeys.length > 0 && filteredRows.length === 0 && <div className='text-center'>No data available.</div>}

                    {filteredRows.length > 0 && selectedKeys.length > 0 && (
                        <div className='w-full flex-1' style={{ minHeight: 400 }}>
                            {isChartView ? (
                                <ResponsiveContainer width='100%' height='100%'>
                                    {chartType === 'bar' ? (
                                        <BarChart data={chartData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <ReTooltip content={getTooltip} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />

                                            {selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return <Bar key={key} dataKey={key} name={key} fill={col} isAnimationActive={false} />;
                                            })}
                                        </BarChart>
                                    ) : (
                                        <LineChart data={chartData} margin={{ top: 10, left: -15, right: 10, bottom: 10 }}>
                                            <CartesianGrid strokeDasharray='3 3' />
                                            <XAxis dataKey='ts' tickMargin={10} tickFormatter={(tick) => format(new Date(tick), 'yyyy.MM.dd.')} tickCount={10} />
                                            <YAxis orientation='left' width={80} tickMargin={10} tickCount={10} type='number' tickFormatter={(tick) => formatLabelValue(tick, { thousandsSeparator: false, decimalPlaces: 2 })} domain={['dataMin', 'dataMax']} />
                                            <ReTooltip content={getTooltip} cursor={{ fill: 'transparent' }} wrapperStyle={{ position: 'absolute', zIndex: 1000 }} />

                                            {selectedKeys.map((key, idx) => {
                                                const col = colorForIndex(idx, selectedKeys.length);
                                                return <Line key={key} type='monotone' dataKey={key} name={key} stroke={col} strokeWidth={2} dot={false} activeDot={{ r: 3 }} />;
                                            })}
                                        </LineChart>
                                    )}
                                </ResponsiveContainer>
                            ) : (
                                <DataTable data={filteredRows} selectedKeys={selectedKeys} />
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </ThemeProvider>
    );
}

function DataTable({ data, selectedKeys }) {
    return (
        <TableContainer component={Paper} style={{ height: '100%' }}>
            <Table className='w-full shadow-2xl' size='small'>
                <TableHead>
                    <TableRow>
                        <TableCell className='text-xs font-semibold'>Timestamp</TableCell>
                        <TableCell className='text-xs font-semibold'>Dynamic Check OK</TableCell>
                        <TableCell className='text-xs font-semibold'>Base Case Succeeded</TableCell>
                        <TableCell className='text-xs font-semibold'>Best Case Succeeded</TableCell>
                        <TableCell className='text-xs font-semibold'>Finished</TableCell>
                        <TableCell className='text-xs font-semibold'>Converged</TableCell>
                        <TableCell className='text-xs font-semibold'>Alarm</TableCell>
                        <TableCell className='text-xs font-semibold'>State</TableCell>
                        <TableCell className='text-xs font-semibold'>Alarm variables count</TableCell>

                        {selectedKeys.map((key) => (
                            <TableCell key={key} align='right' className='text-xs font-semibold whitespace-nowrap'>{key}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {data.map((row, idx) => (
                        <TableRow key={idx}>
                            <TableCell className='whitespace-nowrap text-xs py-12'>{format(new Date(row.ts), 'yyyy.MM.dd. HH:mm:ss')}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{formatYesNo(isDynamicCheckOk(row))}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{formatYesNo(row?.converged === true)}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{formatYesNo(row?.finished === true)}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{formatYesNo(row?.finished === true)}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{formatYesNo(row?.converged === true)}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{formatYesNo(row?.alarm === true)}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{row?.state || '-'}</TableCell>
                            <TableCell className='whitespace-nowrap text-xs'>{alarmVariablesCount(row)}</TableCell>

                            {selectedKeys.map((key) => {
                                const value = normalizeNumber(row?.variables_to_monitor?.[key]);

                                return (
                                    <TableCell key={key} align='right' className='whitespace-nowrap text-xs tabular-nums'>
                                        {value == null ? '-' : formatLabelValue(value, defaultLabelStyle())}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function DatePick(props) {
    return (
        <DatePicker
            className={props.className}
            label={props.label}
            margin='dense'
            value={props.value ?? undefined}
            onChange={e => props.onChange(e)}
            ampm={false}
            minDate={props.minDate}
            maxDate={props.maxDate}
            format='yyyy MMMM d.'
            cancelLabel='Back'
            okLabel='Ok'
            clearLabel='Clear'
            inputVariant='outlined'
            clearable
        />
    );
}